import { config } from "./config";

// 型付き fetch ラッパ（guide-app と同型）。認証必須ルートでは IdToken をモジュール登録の
// ゲッタから自動付与する（AuthContext が registerAuth で登録）。公開ルートや明示上書きが
// 要る場合は opts.token を渡す。

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(
      typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${status}`,
    );
    this.name = "ApiError";
  }
}

type AuthHooks = {
  getToken: () => string | null;
  onUnauthorized: () => void;
  /**
   * F-GUIDE-032（tst-jjcr）。サインイン成功時に自己登録した guide_session.id。
   * 付与すると apps/api の session-guard が端末単位の失効判定（G-14 個別 revoke）を有効化する。
   * 未登録なら全端末一括失効の判定のみになる。
   */
  getSessionId?: () => string | null;
};

// AuthContext が起動時に登録する。未登録時は無認証（公開ルートのみ叩ける）。
let authHooks: AuthHooks = {
  getToken: () => null,
  onUnauthorized: () => {},
};

export function registerAuth(hooks: AuthHooks): void {
  authHooks = hooks;
}

type ApiOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Cognito IdToken を明示指定（省略時は登録済みゲッタから取得）。 */
  token?: string | null;
  signal?: AbortSignal;
};

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  const token = opts.token !== undefined ? opts.token : authHooks.getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  const sessionId = authHooks.getSessionId?.();
  if (sessionId) headers["x-session-id"] = sessionId;

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // 401 = トークン失効/無効。アプリ全体でサインアウトへ誘導する。
  if (res.status === 401) {
    authHooks.onUnauthorized();
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}
